import type React from 'react';
import { createElement } from 'react';
import { renderHook, waitFor } from '@testing-library/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { vi, describe, it, expect, beforeEach, afterEach } from 'vitest';
import { useRouteQuery } from './use-route-query';

const mockSearchParams = new URLSearchParams();
const mockUseSearchParams = vi.fn(() => [mockSearchParams]);

vi.mock('react-router', () => ({
  useSearchParams: () => mockUseSearchParams(),
}));

type QontoClientFn = (endpoint: string, options: unknown) => Promise<unknown>;
const mockQontoClient = vi.fn<QontoClientFn>();
vi.mock('receivable-invoices-js/react/utils/api-clients/qonto', () => ({
  qontoClient: (endpoint: string, options: unknown): Promise<unknown> =>
    mockQontoClient(endpoint, options),
}));

vi.mock('receivable-invoices-js/react/hooks/use-sentry', () => ({
  useSentry: () => ({
    captureException: vi.fn(),
  }),
}));

describe(useRouteQuery, () => {
  let queryClient: QueryClient;

  beforeEach(() => {
    queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          gcTime: 0,
        },
      },
    });
    mockQontoClient.mockClear();
    mockSearchParams.delete('code');
    mockSearchParams.delete('state');
    mockSearchParams.delete('error');
    mockSearchParams.delete('error_description');
  });

  afterEach(() => {
    queryClient.clear();
  });

  const wrapper = ({ children }: { children: React.ReactNode }) =>
    createElement(QueryClientProvider, { client: queryClient }, children);

  describe('error state handling', () => {
    it('should not show error during initial loading state', () => {
      mockSearchParams.set('state', 'test-state');
      mockSearchParams.set('code', 'test-code');

      mockQontoClient.mockImplementation(
        () =>
          new Promise(resolve => {
            setTimeout(() => {
              resolve({
                data: { attributes: { origin: 'https://qonto.com' } },
              });
            }, 100);
          })
      );

      const { result } = renderHook(() => useRouteQuery(), { wrapper });

      expect(result.current.origin).toBeNull();
      expect(result.current.error).toBeNull();
      expect(result.current.isLoading).toBe(true);
    });

    it('should return not_found error when state param is missing', () => {
      mockSearchParams.delete('state');

      const { result } = renderHook(() => useRouteQuery(), { wrapper });

      expect(result.current.origin).toBeNull();
      expect(result.current.error).toBe('not_found');
      expect(result.current.isLoading).toBe(false);
    });

    it('should return query error message when API call fails', async () => {
      mockSearchParams.set('state', 'test-state');
      mockSearchParams.set('code', 'test-code');

      mockQontoClient.mockRejectedValue(new Error('API Error'));

      const { result } = renderHook(() => useRouteQuery(), { wrapper });

      await waitFor(() => {
        expect(result.current.origin).toBeNull();
        expect(result.current.error).toBe('API Error');
        expect(result.current.isLoading).toBe(false);
      });
    });

    it('should return error when origin is missing in response', async () => {
      mockSearchParams.set('state', 'test-state');
      mockSearchParams.set('code', 'test-code');

      mockQontoClient.mockResolvedValue({
        data: { attributes: {} },
      });

      const { result } = renderHook(() => useRouteQuery(), { wrapper });

      await waitFor(() => {
        expect(result.current.origin).toBeNull();
        expect(result.current.error).toBe('Missing origin in response');
        expect(result.current.isLoading).toBe(false);
      });
    });
  });

  describe('successful activation', () => {
    it('should call API with correct params when activation succeeds', async () => {
      mockSearchParams.set('state', 'test-state');
      mockSearchParams.set('code', 'test-code');

      const expectedOrigin = 'https://qonto.com/payment-links';
      mockQontoClient.mockResolvedValue({
        data: { attributes: { origin: expectedOrigin } },
      });

      renderHook(() => useRouteQuery(), { wrapper });

      await waitFor(() => {
        expect(mockQontoClient).toHaveBeenCalledWith(
          'v1/mollie/oauth/redirect',
          expect.objectContaining({
            method: 'GET',
            params: expect.objectContaining({
              state: 'test-state',
              code: 'test-code',
            }) as Record<string, unknown>,
          }) as Record<string, unknown>
        );
      });
    });

    it('should return origin when activation succeeds', async () => {
      mockSearchParams.set('state', 'test-state');
      mockSearchParams.set('code', 'test-code');

      const expectedOrigin = 'https://qonto.com/payment-links';
      mockQontoClient.mockResolvedValue({
        data: { attributes: { origin: expectedOrigin } },
      });

      const { result } = renderHook(() => useRouteQuery(), { wrapper });

      await waitFor(() => {
        expect(result.current.origin).toBe(expectedOrigin);
        expect(result.current.error).toBeNull();
        expect(result.current.isLoading).toBe(false);
      });
    });
  });

  describe('error params', () => {
    it('should pass error params to API', async () => {
      mockSearchParams.set('state', 'test-state');
      mockSearchParams.set('error', 'access_denied');
      mockSearchParams.set('error_description', 'User denied access');

      mockQontoClient.mockResolvedValue({
        data: { attributes: { origin: 'https://qonto.com' } },
      });

      renderHook(() => useRouteQuery(), { wrapper });

      await waitFor(() => {
        expect(mockQontoClient).toHaveBeenCalledWith(
          'v1/mollie/oauth/redirect',
          expect.objectContaining({
            params: expect.objectContaining({
              error: 'access_denied',
              error_description: 'User denied access',
            }) as Record<string, unknown>,
          }) as Record<string, unknown>
        );
      });
    });
  });
});
